import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  peekPurchaseMarker,
  writePurchaseMarker,
  clearPurchaseMarker,
} from '../features/payments/purchaseMarker';
import { invalidateCreditViews } from '../hooks/usePollCreditRefreshAfterPurchase';
import { brandConfig } from '../config/brands';
import { usePageOverlayStore } from '../stores/pageOverlayStore';
import './ProfilePage.css';
import '../styles/buttons.css';

export default function PaymentCompletePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openOverlay = usePageOverlayStore((s) => s.open);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  // ライトテーマ (usodeki 等) では暗色前提のクリーム文字が地に埋もれるため配色を反転する。
  const isLight = brandConfig.colorScheme === 'light';
  const c = {
    heading: isLight ? 'rgba(31, 36, 48, 0.92)' : 'rgba(245, 241, 230, 0.9)',
    body: isLight ? 'rgba(31, 36, 48, 0.6)' : 'rgba(245, 241, 230, 0.7)',
    muted: isLight ? 'rgba(31, 36, 48, 0.45)' : 'rgba(245, 241, 230, 0.5)',
    // 成功見出しはライトテーマ (usodeki 等) では墨インクに寄せ、汎用の緑を避けてブランド配色に馴染ませる。
    success: isLight ? 'rgba(31, 36, 48, 0.92)' : 'rgba(140, 240, 140, 1)',
    error: isLight ? 'rgba(200, 38, 38, 1)' : 'rgba(255, 100, 100, 1)',
    // チェックバッジはブランドアクセント (usodeki=電気紫) で塗り、CTA ボタンと同系色に揃える。org の accent 上書きも尊重。
    check: isLight ? 'var(--brand-accent-solid)' : 'rgba(245, 241, 230, 0.9)',
  };

  // 決済後の戻り先。1 画面構成ブランド (overlayPageNavigation) はダッシュボードを
  // 別ルートに持たないため、ホーム (`/`) へ戻し「利用状況」オーバーレイを開く。
  // 他ブランドは従来の /dashboard。
  const goToUsage = () => {
    if (brandConfig.features.overlayPageNavigation) {
      openOverlay('dashboard');
      navigate('/', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  useEffect(() => {
    const redirectStatus = searchParams.get('redirect_status');
    let redirectTimer: ReturnType<typeof setTimeout> | undefined;

    if (redirectStatus === 'succeeded') {
      setStatus('success');

      // マーカーは新規作成しない。confirmPayment 直前に書かれた既存マーカーを確認・補強し、
      // Dashboard (経路 B) が拾ってポーリングできるようにする。
      const marker = peekPurchaseMarker();
      if (marker) {
        const paymentIntent = searchParams.get('payment_intent') || undefined;
        if (paymentIntent && !marker.paymentIntentId) {
          // 戻りクエリの payment_intent を補強転記 (将来の intent ベース判定の布石)。
          writePurchaseMarker({ ...marker, paymentIntentId: paymentIntent });
        }
      } else {
        // マーカー欠落 (書き込み前に離脱した等の異常系)。
        // 最低限ヘッダー/残高を無効化してから遷移する。
        invalidateCreditViews(queryClient);
      }

      // 3秒後に戻る
      redirectTimer = setTimeout(() => {
        goToUsage();
      }, 3000);
    } else {
      // 決済失敗・中断。マーカーが残っていれば消す (誤起動防止)。
      clearPurchaseMarker();
      setStatus('error');
    }

    // 「今すぐ戻る」で先に離脱した場合などに、3 秒タイマーが後から発火して
    // goToUsage() (usodeki ではオーバーレイ再表示) が再実行されるのを防ぐ。
    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [searchParams, navigate, queryClient]);

  return (
    <div className="page admin-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="profile-container">
        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '18px', color: c.heading }}>
              決済処理を確認中です...
            </div>
            <div style={{ fontSize: '14px', color: c.body, marginTop: '12px' }}>
              しばらくお待ちください
            </div>
          </div>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
              {/* check_badge.svg は白ストローク固定で明色地に埋もれるためインライン化し
                  currentColor でテーマごとに塗り分ける */}
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                style={{ width: '64px', height: '64px', marginBottom: '20px', color: c.check }}
              >
                <path d="m16 9.5 -4.07 5.426a0.767 0.767 0 0 1 -1.155 0.083l-2.442 -2.443" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" />
                <path d="M10.7 1.121a1.665 1.665 0 0 1 2.6 0l1.545 1.923a1.665 1.665 0 0 0 1.479 0.612l2.452 -0.267a1.667 1.667 0 0 1 1.834 1.837l-0.267 2.451a1.666 1.666 0 0 0 0.613 1.479l1.922 1.544a1.666 1.666 0 0 1 0 2.6l-1.922 1.545a1.664 1.664 0 0 0 -0.613 1.478l0.267 2.452a1.665 1.665 0 0 1 -1.836 1.836l-2.452 -0.267a1.668 1.668 0 0 0 -1.479 0.613l-1.543 1.92a1.665 1.665 0 0 1 -2.6 0l-1.543 -1.922a1.668 1.668 0 0 0 -1.479 -0.613l-2.452 0.267a1.665 1.665 0 0 1 -1.836 -1.836l0.267 -2.452a1.664 1.664 0 0 0 -0.613 -1.478L1.122 13.3a1.666 1.666 0 0 1 0 -2.6l1.922 -1.544a1.666 1.666 0 0 0 0.613 -1.479L3.39 5.226a1.667 1.667 0 0 1 1.836 -1.837l2.452 0.267a1.665 1.665 0 0 0 1.479 -0.612Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" />
              </svg>
              <div style={{ fontSize: '20px', color: c.success, marginBottom: '12px', fontWeight: 600 }}>
                ポイント購入が完了しました
              </div>
              <div style={{ fontSize: '14px', color: c.body, marginBottom: '24px' }}>
                ポイントがアカウントに追加されました
              </div>
              <div style={{ fontSize: '14px', color: c.muted }}>
                3秒後に戻ります...
              </div>
              <button
                onClick={goToUsage}
                className={isLight ? 'btn-primary' : 'btn-modal'}
                style={{ marginTop: '24px', padding: '10px 24px' }}
              >
                今すぐ戻る
              </button>
            </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px', color: c.error }}>✕</div>
            <div style={{ fontSize: '20px', color: c.error, marginBottom: '12px', fontWeight: 600 }}>
              決済に失敗しました
            </div>
            <div style={{ fontSize: '14px', color: c.body, marginBottom: '24px' }}>
              決済処理中にエラーが発生しました
            </div>
            <button
              onClick={goToUsage}
              className={isLight ? 'btn-primary' : 'btn-modal'}
              style={{ marginTop: '24px', padding: '10px 24px' }}
            >
              戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
